import {
  Box,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  ScreenError,
  ScreenWrap,
  SearchInput,
  SelectInput,
  SoloLoading,
} from '../../components';
import AppUsersRow from './AppUsersRow';
import ProgramStatusFilter from './ProgramStatusFilter';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router';
import { Api } from '../../api';
import { getPlanDurationLabel } from '../../models/Subscription';
import { useApiRead } from 'api-read-hook';
import { HydraCollection } from '../../types';
import AppleIcon from '../../components/icons/AppleIcon';
import B2BIcon from '../../components/icons/B2BIcon';
import ManualIcon from '../../components/icons/ManualIcon';
import StripeIcon from '../../components/icons/StripeIcon';

type OrganisationTeam = {
  '@id': string;
  '@type': 'OrganisationTeam';
  id: string;
  name: string;
  organisation: {
    '@id': string;
    '@type': 'Organisation';
    name: string;
  };
};

export default function AppUsersListScreen() {
  const {
    getParams,
    setModifiedParams,
    tablePaginationProps,
    tableSortLabelProps,
  } = useModifiableParams();

  const params = getParams();

  const api = Api.User.useReadCollection(params);
  const classificationsApi = Api.ClientClassification.useReadCollection();
  const cliniciansApi = Api.User.useReadCollectionClinicians({
    ['pagination' as any]: false,
  });
  const organisationsApi = useApiRead<HydraCollection<OrganisationTeam>>(
    '/organisation-teams',
  );

  if (api.error) return <ScreenError {...api} />;
  if (classificationsApi.error) return <ScreenError {...classificationsApi} />;
  if (cliniciansApi.error) return <ScreenError {...cliniciansApi} />;
  if (organisationsApi.error) return <ScreenError {...organisationsApi} />;

  const assignedCoachOptions = [
    { label: 'All', value: '' },
    ...(cliniciansApi.data?.member || [])
      .filter((c) => c.clinicianProfile?.type === 'coach')
      .map((c) => ({ label: c.name ?? c.email, value: c.id })),
  ];

  const lmPhysicianOptions = [
    { label: 'All', value: '' },
    ...(cliniciansApi.data?.member || [])
      .filter((c) => c.clinicianProfile?.type === 'physician')
      .map((c) => ({ label: c.name ?? c.email, value: c.id })),
  ];

  const b2bOptions = [
    { label: 'All', value: '' },
    ...(organisationsApi.data?.member || []).map((team) => ({
      label: `${team.organisation.name} - ${team.name}`,
      value: team.id,
    })),
  ];

  return (
    <ScreenWrap>
      <Stack spacing={3} alignItems="stretch">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" component="h1">
            App Users
          </Typography>
          <Button
            variant="text"
            endIcon={<ArrowForward />}
            component={Link}
            to="/user-classifications"
          >
            Show & Edit User Classifications
          </Button>
        </Stack>
        <Stack
          direction="row"
          alignItems="end"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Box minWidth={360}>
            <SearchInput
              label="Search"
              value={params.search || ''}
              onChange={(e) =>
                setModifiedParams({ search: e.target.value, page: '1' })
              }
              placeholder="Search by name or email"
            />
          </Box>
          <ProgramStatusFilter
            program={params.programStatusProgram || ''}
            status={params.programStatus || ''}
            onChange={({ program, status }) =>
              setModifiedParams({
                programStatusProgram: program,
                programStatus: status,
                page: '1',
              })
            }
          />
          <SelectInput
            label="Assigned Coach"
            labelId="assigned-coach"
            value={params.coach || ''}
            onChange={(value) => setModifiedParams({ coach: value, page: '1' })}
            options={assignedCoachOptions}
          />
          <SelectInput
            label="LM Physician"
            labelId="lm-physician"
            value={params.physician || ''}
            onChange={(value) =>
              setModifiedParams({ physician: value, page: '1' })
            }
            options={lmPhysicianOptions}
          />
          <SelectInput
            label="Sub. Status"
            labelId="sub-status"
            value={params.subStatus || ''}
            onChange={(value) =>
              setModifiedParams({ subStatus: value, page: '1' })
            }
            options={[
              { label: 'All', value: '' },
              { label: 'Active', value: 'active' },
              { label: 'Active (ending)', value: 'active_ending' },
              { label: 'Inactive', value: 'inactive' },
              { label: 'Licence revoked', value: 'licence_revoked' },
              { label: 'Payment failed', value: 'payment_failed' },
              { label: 'Payment overdue', value: 'payment_overdue' },
              { label: 'Pending', value: 'pending' },
            ]}
          />
          <SelectInput
            label="Sub. Source"
            labelId="sub-source"
            value={params.subSource || ''}
            onChange={(value) =>
              setModifiedParams({ subSource: value, page: '1' })
            }
            options={[
              { label: 'All', value: '' },
              { label: 'Stripe', value: 'stripe', icon: <StripeIcon /> },
              {
                label: 'In-App Purchase',
                value: 'in_app',
                icon: <AppleIcon />,
              },
              { label: 'Manual', value: 'manual', icon: <ManualIcon /> },
              { label: 'B2B', value: 'b2b', icon: <B2BIcon /> },
            ]}
          />
          <SelectInput
            label="Plan"
            labelId="plan"
            value={params.plan || ''}
            onChange={(value) => setModifiedParams({ plan: value, page: '1' })}
            options={[
              { label: 'All', value: '' },
              ...(['four_month', 'twelve_month'] as const).map((value) => ({
                label: getPlanDurationLabel(value) ?? value,
                value,
              })),
            ]}
          />
          <SelectInput
            label="B2B"
            labelId="b2b"
            value={params.organisationTeam || ''}
            onChange={(value) =>
              setModifiedParams({ organisationTeam: value, page: '1' })
            }
            options={b2bOptions}
          />
          <SelectInput
            label="Classifications"
            labelId="classifications"
            value={params.classifications || ''}
            onChange={(value) =>
              setModifiedParams({ classifications: value, page: '1' })
            }
            options={[
              { label: 'All', value: '' },
              ...(classificationsApi.data?.member || []).map((c) => ({
                label: c.name,
                value: c.id,
              })),
            ]}
          />
        </Stack>

        <Stack spacing={2}>
          <Card>
            {api.data?.member ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Program Status</TableCell>
                      <TableCell>
                        <TableSortLabel
                          {...tableSortLabelProps('registeredAt')}
                        >
                          Registration Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Assigned Coach</TableCell>
                      <TableCell>LM Physician</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>B2B</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {api.data.member.map((u) => (
                      <AppUsersRow
                        key={u.user.id}
                        appUser={u}
                        showProgramTitle={!params.programStatusProgram}
                      />
                    ))}
                  </TableBody>
                  <TableFooter
                    sx={{
                      borderTop: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <TableRow>
                      <TablePagination
                        {...tablePaginationProps({
                          totalItems: api.data?.totalItems ?? 0,
                          rowsPerPage: 10,
                        })}
                        count={api.data?.totalItems ?? 0}
                        colSpan={9}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <SoloLoading />
            )}
          </Card>
        </Stack>
      </Stack>
    </ScreenWrap>
  );
}
